import React, { Fragment, forwardRef, HTMLProps, memo } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { twJoin } from "tailwind-merge";

import { HeightSize, WidthSize } from "src/design-system/types";
import { assertUnreachable } from "src/utils/typeUtils";

// Link to the Figma file with the variants and sizes: https://www.figma.com/file/M6oWoeOynJBBK0xppg19h9/Decide-Redesign%3A-Deliverables?node-id=1473-6255&t=2LixZSZKq1NoiHy4-11

export type IconSizes =
  | "3xs" // 8px
  | "2xs" // 10px
  | "xs" // 12px
  | "sm" // 14px
  | "md" // 16px
  | "base" // 16px
  | "lg" // 18px
  | "xl" // 20px
  | "2xl"; // 24px

type BaseProps = {
  icon: IconProp;
  size?: IconSizes;
  color?: string;
  spin?: boolean;
  dataLoc?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseDownCapture?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
};

type ButtonIconProps = BaseProps &
  Pick<HTMLProps<HTMLButtonElement>, "onClick" | "disabled">;

type DivIconProps = {
  onClick?: never;
} & BaseProps;

export type IconProps = ButtonIconProps | DivIconProps;

/**
 * @deprecated Use `Icon` from `src/design-system/Icon` instead.
 */
export const Icon = memo(
  forwardRef<HTMLSpanElement, IconProps>(
    (
      {
        icon,
        size = "base",
        color,
        spin,
        dataLoc,
        onClick,
        onMouseDownCapture,
        // spreading the remaining DOM props allows the use of asChild for library components like the tooltip:
        // https://github.com/radix-ui/primitives/issues/953
        ...restOfDomProps
      },
      ref,
    ) => {
      const Wrapper =
        onClick !== undefined || onMouseDownCapture !== undefined
          ? "button"
          : Fragment;

      const { width, height } = getIconSize(size);
      return (
        // The conditional passing of button props is required because they are not allowed to be set at all on a Fragment, not even to undefined.
        <Wrapper
          {...(Wrapper === "button" && {
            onClick,
            onMouseDownCapture,
            type: "button",
            tabIndex: 0,
            ...restOfDomProps,
          })}
        >
          <span
            ref={ref}
            className={twJoin(
              "flex cursor-default justify-center",
              "disabled" in restOfDomProps &&
                restOfDomProps["disabled"] === true &&
                "cursor-not-allowed",
              "p-1",
            )}
            {...(Wrapper === Fragment && { ...restOfDomProps })}
          >
            <FontAwesomeIcon
              className={twJoin("aspect-square", width, height, color)}
              data-loc={dataLoc}
              icon={icon}
              spin={spin}
            />
          </span>
        </Wrapper>
      );
    },
  ),
);

export const getIconSize = (
  size: IconSizes,
): { width: WidthSize; height: HeightSize } => {
  switch (size) {
    case "3xs":
      return {
        width: "w-2",
        height: "h-2",
      };
    case "2xs":
      return {
        width: "w-2.5",
        height: "h-2.5",
      };
    case "xs":
      return {
        width: "w-3",
        height: "h-3",
      };
    case "sm":
      return {
        width: "w-3.5",
        height: "h-3.5",
      };
    case "base":
    case "md":
      return {
        width: "w-4",
        height: "h-4",
      };
    case "lg":
      return {
        width: "w-4.5",
        height: "h-4.5",
      };
    case "xl":
      return {
        width: "w-5",
        height: "h-5",
      };
    case "2xl":
      return {
        width: "w-6",
        height: "h-6",
      };
    default:
      return assertUnreachable(size);
  }
};
